import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { RuleKind } from '@/shared/api/generated/rules';
import { TextField } from '@/shared/ui/Field';

import { KIND_SHAPES, invalidParameters } from '../model/parameters';

/* The inputs one rule kind needs, and nothing else.
 *
 * **The fields are driven by the kind rather than written out per form**, which is what makes the two
 * forms that use this agree: authoring and publishing a later version ask for the same parameters,
 * because they are the same version payload. Two hand-written sets would drift the first time a kind
 * gained a parameter, and the symptom is a server refusal with no field to satisfy it — which is the
 * defect this component was written to fix.
 *
 * **A kind with no parameters renders nothing at all**, not an empty fieldset with a heading. `not_null`
 * needs only a column, and a "Parameters" heading over nothing invites an author to look for a field
 * that does not exist.
 *
 * Errors are shown per field and only after the author has typed something into it. Marking every
 * required field red before anybody has touched the form is how a form reads as broken on open.
 */

export interface RuleParameterFieldsProps {
  readonly kind: RuleKind;
  readonly values: Readonly<Record<string, string>>;
  readonly onChange: (name: string, value: string) => void;
  /** `true` once a submit has been attempted, so untouched required fields may show their error. */
  readonly submitted: boolean;
}

export function RuleParameterFields({
  kind,
  values,
  onChange,
  submitted,
}: RuleParameterFieldsProps): ReactElement | null {
  const { t } = useTranslation();
  const shape = KIND_SHAPES[kind];
  const problems = invalidParameters(kind, values);

  if (shape.parameters.length === 0) return null;

  return (
    <div className="lq-grid">
      {shape.parameters.map((parameter) => {
        const value = values[parameter.name] ?? '';
        const problem = problems.get(parameter.name);
        /* Shown once the field has content — so a malformed value is flagged as it is typed — or once
         * a submit has been attempted, which is when an empty required field becomes news. */
        const visible = problem !== undefined && (submitted || value.trim() !== '');
        return (
          <TextField
            key={parameter.name}
            label={t(parameter.labelKey)}
            value={value}
            onChange={(next) => {
              onChange(parameter.name, next);
            }}
            required
            /* `type="number"` deliberately *not* set for the numeric parameters. `max_age_hours` and
             * a range bound both reach the server as strings — `expression.parameters` is
             * `Record<string, string>` — and a number input silently drops a value a browser cannot
             * parse, which would send an empty string where the author typed something. The check in
             * `invalidParameters` reports it instead, with the value still on screen. */
            hint={visible ? t(`rules.param.problem.${problem}`) : t(parameter.hintKey)}
            placeholder={t(parameter.placeholderKey)}
            maxLength={parameter.input === 'sql' ? 4000 : 500}
          />
        );
      })}
    </div>
  );
}
